import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import CurrentPackagesTableCell from 'components/AthletePackages/CurrentPackagesTableCell';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import usePayingMembers from 'hooks/reports/athletes/usePayingMembers';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { MemberReport } from 'types';
import { formatGymDate } from 'utils/date-functions';

import { SearchFilters } from '../types';

const PayingMembersTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const navigate = useNavigate();
  const { dateFormat } = useLocalization();
  const { session } = useAuth();

  const [ordering, setOrdering] = useState('firstName');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<MemberReport[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePayingMembers({
      ordering: ascending ? ordering : `-${ordering}`,
      date: searchFilters.date.toISOString(),
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleNameClick = useCallback(
    (athlete: MemberReport): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<MemberReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleNameClick(row.original)}
        >
          {row.original.fullName}
        </Text>
      ),
    },
    {
      Header: 'Current Status',
      accessor: 'athleteStatus' as const,
      disableSortBy: true,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
      disableSortBy: true,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      disableSortBy: true,
    },
    {
      Header: 'Member Since',
      accessor: 'memberSince' as const,
      // ENG-2344: render the gym-local join date so the table matches the CSV export, not the
      // viewer's browser-tz date.
      Cell: ({ value }: CellProps<MemberReport>) =>
        value ? formatGymDate(value, session?.gym?.timezone, dateFormat) : '',
    },
    {
      Header: 'Current Packages',
      accessor: 'currentPackages' as const,
      disableSortBy: true,
      Cell: ({ row }: CellProps<MemberReport>) => (
        <CurrentPackagesTableCell currentPackages={row.original.currentPackages ?? []} />
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'phone', 'memberSince']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default PayingMembersTable;
